import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCourseById } from '../../data/courses';
import { fetchMyCohorts, type Cohort } from '../../services/cohortService';
import MatrixBackground from '../../components/ui/MatrixBackground';

const badgeColors: Record<string, string> = {
  green: 'bg-green-500/15 text-green-400',
  yellow: 'bg-yellow-500/15 text-yellow-400',
  purple: 'bg-purple-500/15 text-purple-400',
  blue: 'bg-blue-500/15 text-blue-400',
};

export default function Bootcamps() {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyCohorts()
      .then(setCohorts)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-black font-sans">
      <nav className="fixed top-0 w-full bg-black/80 backdrop-blur-md border-b border-green-500/30 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2 text-green-400/70 hover:text-green-300 transition-colors text-sm font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M15 19l-7-7 7-7" /></svg>
              Volver al inicio
            </Link>
            <Link to="/profile" className="text-green-400/70 hover:text-green-300 transition-colors text-sm font-medium">
              Mi perfil
            </Link>
          </div>
        </div>
      </nav>

      <section className="relative pt-28 pb-10 px-6">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/70 z-[1]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-extrabold tracking-tight mb-3">
            <span className="text-white">Mis Bootcamps</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-base md:text-lg text-gray-300 max-w-xl mx-auto">
            Programas intensivos de Data Engineering con clases, ejercicios y certificaci&oacute;n
          </motion.p>
        </div>
      </section>

      <section className="relative pb-24 px-6">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/70 z-[1]" />
        <div className="relative z-10 max-w-5xl mx-auto">
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-8 h-8 border-2 border-green-500/30 border-t-green-400 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {cohorts.map((cohort, i) => {
                const course = getCourseById(cohort.bootcamp_id);
                if (!course) return null;

                return (
                  <motion.div
                    key={cohort.cohort_id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                  >
                    <Link
                      to={`/bootcamps/${course.id}/${cohort.cohort_code}`}
                      className="group flex flex-col h-full bg-gray-900 rounded-lg overflow-hidden shadow-xl transition-all duration-300 ease-in-out hover:-translate-y-1 hover:ring-2 hover:ring-green-500/40"
                    >
                      <div className="relative">
                        <div className={`h-44 bg-gradient-to-br ${course.gradient} flex items-center justify-center`}>
                          <span className="text-6xl font-black text-white/[0.07] select-none group-hover:text-white/[0.12] transition-colors duration-300">
                            {course.iconText}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-1 flex-col justify-between gap-3 px-5 pb-5 pt-3">
                        <div>
                          <div className="inline">
                            <span className="align-middle text-lg font-bold text-green-400 group-hover:underline">{course.title}</span>
                            {' '}
                            <span className={`inline-block rounded px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider ${badgeColors[course.badgeColor]}`}>
                              {cohort.cohort_name}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 pb-4 pt-2">
                          <span className="flex items-center gap-1.5 rounded-full bg-gray-800 px-3 py-1.5 text-xs font-medium text-white">
                            {course.weeks}
                          </span>
                          <span className="flex items-center gap-1.5 rounded-full bg-gray-800 px-3 py-1.5 text-xs font-medium text-white">
                            {course.totalClasses} Clases
                          </span>
                        </div>

                        <div className="mt-auto flex w-full items-center justify-end">
                          <span className="inline-flex items-center gap-2 rounded-md bg-green-500 px-4 py-2 text-sm font-semibold text-black transition-colors group-hover:bg-green-400">
                            Ingresar
                            <svg className="w-4 h-4 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth={2.5}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}

              {cohorts.length === 0 && (
                <div className="col-span-full text-center py-16">
                  <p className="text-gray-500 text-lg">No ten&eacute;s bootcamps asignados todav&iacute;a.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
